/**
 * Every edge terminal session: who opened a shell, on which node, at which
 * address, for how long, how it ended — and the recording, replayable here.
 *
 * Owner-only on the server (list, detail and recording). A non-owner sees the
 * 403 explained rather than an empty table, which would read as "nobody has
 * ever opened a shell".
 *
 * Three things this page is careful about, because a recording is the most
 * sensitive thing the panel stores:
 *
 * * **The row outlives the recording.** Recordings are pruned after the
 *   retention period, and the table says `pruned` rather than dropping the
 *   row: who opened a shell where is itself the record, and the database
 *   refuses to delete it.
 * * **Replay is in a panel, from the server's verified copy.** Every replay is
 *   audited (`terminal.recording.view`), and a recording that fails its
 *   integrity check is refused, never shown half-trusted.
 * * **Download is armed.** The file is the whole session — every keystroke,
 *   passwords included, and all output, which on a hospital box may be
 *   patient data. Once saved it is outside every control this panel has, so
 *   it takes a deliberate second click after a warning that says so, and the
 *   download is audited separately from a view.
 *
 * Filters live in the URL (`?node=&actor=&days=`) so the node page can link
 * straight to "all sessions on this node", and `/terminal-sessions/:id` opens
 * one session's panel directly, so a session is addressable from an audit row.
 */

import { lazy, Suspense, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Badge, type Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { MeshSourceBadge } from '../components/EdgeAddresses';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar } from '../components/ui/FilterBar';
import { Select, SelectItem } from '../components/ui/Form';
import { DownloadIcon } from '../components/ui/Icon';
import { ApiError, apiFetchText, describeError } from '../lib/api';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  EdgeNodeListResponse,
  RecordingState,
  TerminalSessionListResponse,
  TerminalSessionResponse,
} from '../types/api';

import '../styles/page-terminal.css';

// xterm is ~300 KB; only the replay panel needs it.
const TerminalReplay = lazy(() =>
  import('../components/TerminalReplay').then((module) => ({
    default: module.TerminalReplay,
  })),
);

const REFRESH_MS = 30_000;
const DAY_OPTIONS = [1, 7, 30, 90, 365] as const;
const DEFAULT_DAYS = 30;
const LIMIT = 500;

const RECORDING_TONE: Record<string, Tone> = {
  recording: 'info',
  available: 'good',
  interrupted: 'warning',
  pruned: 'neutral',
  missing: 'critical',
};

const RECORDING_HINT: Record<string, string> = {
  recording: 'The session is still open and being recorded.',
  available: 'Sealed and verified at the end of the session; replayable.',
  interrupted: 'The session was cut off (controller restart or recording failure); the recording has no end marker but is replayable.',
  pruned: 'The recording passed the retention period and was deleted. The session record is kept.',
  missing: 'The row points at a recording file that is not on disk.',
};

export function RecordingBadge({ state }: { state: RecordingState }) {
  return (
    <Badge tone={RECORDING_TONE[state] ?? 'neutral'} small title={RECORDING_HINT[state]}>
      {state}
    </Badge>
  );
}

/** `client_closed` / `client-closed` → "client closed". Either spelling reads. */
export function exitLabel(reason: string | null): string {
  if (!reason) return 'open';
  return reason.replace(/[_-]+/g, ' ');
}

const EXIT_TONE: Record<string, Tone> = {
  client_closed: 'neutral',
  remote_closed: 'neutral',
  idle_timeout: 'neutral',
  max_duration: 'warning',
  connect_failed: 'warning',
  host_key_mismatch: 'critical',
  recording_failed: 'critical',
  recording_limit: 'warning',
  interrupted: 'warning',
  shutdown: 'warning',
  error: 'critical',
};

function exitTone(reason: string | null): Tone {
  if (!reason) return 'info';
  return EXIT_TONE[reason.replace(/-/g, '_')] ?? 'neutral';
}

export function isReplayable(state: RecordingState): boolean {
  return state === 'available' || state === 'interrupted';
}

function sessionPath(id: number, search: string): string {
  return `/terminal-sessions/${id}${search ? `?${search}` : ''}`;
}

function nodeName(s: TerminalSessionResponse): string {
  return s.node_label || s.node_uuid.slice(0, 8);
}

export function TerminalSessionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');

  const node = params.get('node') ?? '';
  const actor = params.get('actor') ?? '';
  const daysParam = Number(params.get('days'));
  const days = (DAY_OPTIONS as readonly number[]).includes(daysParam) ? daysParam : DEFAULT_DAYS;

  const query = new URLSearchParams();
  if (node) query.set('node_uuid', node);
  if (actor) query.set('actor', actor);
  query.set('days', String(days));
  query.set('limit', String(LIMIT));

  const sessions = useResource<TerminalSessionListResponse>(
    `/api/terminal/sessions?${query.toString()}`,
    { refreshMs: REFRESH_MS },
  );
  // Viewer-readable, so it loads for anyone; used only to name the node filter.
  const nodes = useResource<EdgeNodeListResponse>('/api/edge-nodes');

  const openId = id && /^\d+$/.test(id) ? Number(id) : null;
  const rows = useMemo(() => sessions.data?.sessions ?? [], [sessions.data]);
  const inList = openId !== null ? rows.find((r) => r.id === openId) ?? null : null;
  // A deep link to a session outside the current filters still opens: the
  // single-session route is asked for it.
  const single = useResource<TerminalSessionResponse>(
    `/api/terminal/sessions/${openId ?? 0}`,
    // Once the list has settled either way: a failed list (403 for a
    // non-owner, a network error) must not leave the panel loading forever.
    {
      enabled:
        openId !== null && inList === null && (Boolean(sessions.data) || Boolean(sessions.error)),
    },
  );
  // useResource keeps the previous path's data across a path change and a
  // failed refetch, so only trust it when it is the session the URL names.
  const singleData =
    openId !== null && single.data && single.data.id === openId ? single.data : null;
  const openSession = inList ?? singleData;

  const actors = useMemo(() => {
    const set = new Set(rows.map((r) => r.actor_email));
    if (actor) set.add(actor);
    return [...set].sort();
  }, [rows, actor]);

  const setFilter = (key: 'node' | 'actor' | 'days', value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const keepSearch = params.toString();
  const close = () => navigate(`/terminal-sessions${keepSearch ? `?${keepSearch}` : ''}`);

  const forbidden = sessions.error instanceof ApiError && sessions.error.status === 403;
  const stale = Boolean(sessions.error && sessions.data);

  const columns: Column<TerminalSessionResponse, string>[] = [
    {
      key: 'started_at',
      header: 'Started',
      render: (s) => (
        <Link
          to={sessionPath(s.id, keepSearch)}
          onClick={(event) => event.stopPropagation()}
          title={formatDateTime(s.started_at)}
        >
          #{s.id} · {formatRelative(s.started_at)}
        </Link>
      ),
    },
    {
      key: 'node',
      header: 'Node',
      value: (s) => `${s.node_label ?? ''} ${s.node_uuid} ${s.center_slug ?? ''}`,
      render: (s) => (
        <>
          <Link
            to={`/edge-nodes/${encodeURIComponent(s.node_uuid)}`}
            onClick={(event) => event.stopPropagation()}
          >
            {nodeName(s)}
          </Link>
          {s.center_slug ? <span className="muted"> · {s.center_slug}</span> : null}
        </>
      ),
    },
    { key: 'actor_email', header: 'Opened by' },
    {
      key: 'address',
      header: 'Address',
      render: (s) => (
        <>
          <span className="mono">
            {s.address || UNKNOWN}
          </span>{' '}
          <MeshSourceBadge source={s.address_source} />
        </>
      ),
    },
    {
      key: 'duration_s',
      header: 'Duration',
      numeric: true,
      render: (s) => (s.ended_at ? formatDuration(s.duration_s) : 'open'),
    },
    {
      key: 'bytes_out',
      header: 'Output',
      numeric: true,
      render: (s) => formatBytes(s.bytes_out),
    },
    {
      key: 'exit_reason',
      header: 'Ended',
      value: (s) => s.exit_reason ?? '',
      render: (s) => (
        <Badge tone={exitTone(s.exit_reason)} small>
          {exitLabel(s.exit_reason)}
        </Badge>
      ),
    },
    {
      key: 'recording_state',
      header: 'Recording',
      render: (s) => <RecordingBadge state={s.recording_state} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Terminal sessions"
        subtitle="Every shell opened on an edge node from this panel, with its recording. Owner only."
        actions={
          <Freshness
            checkedAt={sessions.fetchedAt}
            label="Fetched"
            refreshing={sessions.refreshing}
            onRefresh={sessions.reload}
          />
        }
      />

      {forbidden ? (
        <Callout tone="warning" title="Owner role required">
          Session records and their recordings can hold patient data and typed
          passwords, so only owners can list or replay them. Each session&rsquo;s
          start and end is still in the audit log, without addresses.
        </Callout>
      ) : (
        <>
          {stale ? <StaleBanner error={sessions.error} onRetry={sessions.reload} /> : null}
          {sessions.data?.reason ? (
            <Callout tone="warning" title="The session list may be incomplete">
              {sessions.data.reason}
            </Callout>
          ) : null}

          <FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Search node, actor, address, reason…"
            searchLabel="Search terminal sessions"
            count={`${formatNumber(rows.length)} ${pluralise(rows.length, 'session')}${
              rows.length >= LIMIT ? ` (newest ${formatNumber(LIMIT)})` : ''
            }`}
          >
            <Select
              id="sessions-node"
              labelText="Node"
              hideLabel
              value={node}
              onChange={(event) => setFilter('node', event.target.value)}
            >
              <SelectItem value="" text="All nodes" />
              {(nodes.data?.nodes ?? []).map((n) => (
                <SelectItem
                  key={n.node_uuid}
                  value={n.node_uuid}
                  text={n.label || n.node_uuid.slice(0, 8)}
                />
              ))}
              {node && !(nodes.data?.nodes ?? []).some((n) => n.node_uuid === node) ? (
                <SelectItem value={node} text={node.slice(0, 8)} />
              ) : null}
            </Select>
            <Select
              id="sessions-actor"
              labelText="Opened by"
              hideLabel
              value={actor}
              onChange={(event) => setFilter('actor', event.target.value)}
            >
              <SelectItem value="" text="Anyone" />
              {actors.map((a) => (
                <SelectItem key={a} value={a} text={a} />
              ))}
            </Select>
            <Select
              id="sessions-days"
              labelText="Period"
              hideLabel
              value={String(days)}
              onChange={(event) =>
                setFilter('days', event.target.value === String(DEFAULT_DAYS) ? '' : event.target.value)
              }
            >
              {DAY_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)} text={d === 1 ? 'Last 24 hours' : `Last ${d} days`} />
              ))}
            </Select>
          </FilterBar>

          {sessions.loading && !sessions.data ? (
            <SkeletonRows rows={4} />
          ) : sessions.error && !sessions.data ? (
            <ErrorBlock what="the terminal sessions" error={sessions.error} onRetry={sessions.reload} />
          ) : (
            <Card flush>
              <DataTable<TerminalSessionResponse, string>
                rows={rows}
                columns={columns}
                getRowId={(s) => String(s.id)}
                label="Terminal sessions"
                initialSortKey="started_at"
                initialSortDirection="desc"
                search={search}
                onRowClick={(s) => navigate(sessionPath(s.id, keepSearch))}
                isRowActive={(s) => s.id === openId}
                empty={
                  <EmptyBlock title="No terminal sessions in this period">
                    Nobody has opened a shell on {node ? 'this node' : 'an edge node'} from
                    this panel in the selected period.
                    {sessions.data ? ` Recordings are kept ${sessions.data.retention_days} days; the session records are kept for good.` : ''}
                  </EmptyBlock>
                }
              />
            </Card>
          )}
        </>
      )}

      <DetailPanel
        open={openId !== null}
        onClose={close}
        width="min(64rem, 96vw)"
        title={openSession ? `Session #${openSession.id}` : `Session #${openId ?? ''}`}
        subtitle={
          openSession
            ? `${nodeName(openSession)} · ${openSession.actor_email} · ${formatDateTime(openSession.started_at)}`
            : undefined
        }
        footer={
          openSession && isReplayable(openSession.recording_state) ? (
            <DownloadRecording key={openSession.id} session={openSession} />
          ) : undefined
        }
      >
        {openSession ? (
          <SessionDetail session={openSession} />
        ) : forbidden ? (
          <Callout tone="warning" title="Owner role required">
            Only owners can open session records and their recordings.
          </Callout>
        ) : single.error && !single.loading && !single.refreshing ? (
          <Callout tone="critical" title="Could not load this session">
            {describeError(single.error)}
          </Callout>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </DetailPanel>
    </>
  );
}

function SessionDetail({ session: s }: { session: TerminalSessionResponse }) {
  return (
    <div className="stack">
      <dl className="dl">
        <dt>Node</dt>
        <dd>
          <Link to={`/edge-nodes/${encodeURIComponent(s.node_uuid)}`}>{nodeName(s)}</Link>
          {s.center_slug ? <span className="muted"> · {s.center_slug}</span> : null}
        </dd>
        <dt>Opened by</dt>
        <dd>{s.actor_email}</dd>
        <dt>Address</dt>
        <dd>
          <span className="mono">
            {s.address ? `${s.ssh_user ? `${s.ssh_user}@` : ''}${s.address}` : UNKNOWN}
          </span>{' '}
          <MeshSourceBadge source={s.address_source} />
        </dd>
        <dt>Host key</dt>
        <dd>
          <span className="mono">{s.host_key_fingerprint || UNKNOWN}</span>
        </dd>
        <dt>Started</dt>
        <dd>{formatDateTime(s.started_at)}</dd>
        <dt>Connected</dt>
        <dd>{s.connected_at ? formatDateTime(s.connected_at) : <span className="muted">never connected</span>}</dd>
        <dt>Ended</dt>
        <dd>
          {s.ended_at ? formatDateTime(s.ended_at) : <span className="muted">still open</span>}{' '}
          <Badge tone={exitTone(s.exit_reason)} small>
            {exitLabel(s.exit_reason)}
          </Badge>
        </dd>
        <dt>Duration</dt>
        <dd>{s.ended_at ? formatDuration(s.duration_s) : UNKNOWN}</dd>
        <dt>Traffic</dt>
        <dd>
          {formatBytes(s.bytes_in)} typed · {formatBytes(s.bytes_out)} output
        </dd>
        <dt>Recording</dt>
        <dd>
          <RecordingBadge state={s.recording_state} />
        </dd>
      </dl>

      {isReplayable(s.recording_state) ? (
        <Suspense fallback={<p className="muted">Loading the player…</p>}>
          <TerminalReplay sessionId={s.id} />
        </Suspense>
      ) : s.recording_state === 'recording' ? (
        <Callout tone="info" title="The session is still open">
          The recording is sealed when it ends; it can be replayed then.
        </Callout>
      ) : (
        <Callout tone={s.recording_state === 'missing' ? 'critical' : 'info'} title="No replay available">
          {RECORDING_HINT[s.recording_state] ?? `Recording state: ${s.recording_state}.`}
        </Callout>
      )}
    </div>
  );
}

/**
 * The armed download. The first click only arms it and states plainly what
 * the file is; the second fetches the verified cast (audited as a download,
 * not a view) and hands it to the browser as a Blob.
 */
function DownloadRecording({ session }: { session: TerminalSessionResponse }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const { text } = await apiFetchText(
        `/api/terminal/sessions/${session.id}/cast?download=true`,
      );
      const blob = new Blob([text], { type: 'application/x-asciicast;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `terminal-session-${session.id}.cast`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setArmed(false);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {error ? (
        <Callout tone="critical" title="Could not download the recording">
          {error}
        </Callout>
      ) : null}
      {armed ? (
        <>
          <Callout tone="warning" title="This file may contain patient data and passwords">
            It is the whole session, unencrypted: every keystroke — including any
            password typed at a prompt — and all output, which on a hospital node
            can include patient names and study details. Once saved it is outside
            this panel&rsquo;s retention and access control. The download is
            recorded in the audit log under your name.
          </Callout>
          <div className="toolbar">
            <Button kind="danger" size="sm" renderIcon={DownloadIcon} onClick={download} disabled={busy}>
              {busy ? 'Downloading…' : 'Download unencrypted .cast'}
            </Button>
            <Button kind="tertiary" size="sm" onClick={() => setArmed(false)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <div className="toolbar">
          <Button kind="ghost" size="sm" renderIcon={DownloadIcon} onClick={() => setArmed(true)}>
            Download recording…
          </Button>
        </div>
      )}
    </div>
  );
}
